import React, { useState, useRef, useEffect } from 'react';
import {
  MdOutlineMail,
  MdOutlineLock,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
  MdArrowBack,
  MdCheckCircle,
  MdOutlineSlowMotionVideo,
  MdOutlineLibraryMusic,
  MdOutlinePhone,
  MdRefresh,
} from 'react-icons/md';
import { HiOutlineUser } from 'react-icons/hi2';
import logoPng from './assets/speedotube_logo_2ddark.png';
import logo2dLight from './assets/speedotube_logo_2dlight.png';
import { getAvatarColor } from './utils/avatarUtils';
import { API_BASE_URL } from './config';
import './AuthPage.css';

const GOOGLE_CLIENT_ID = '319840528092-mcddjsn5su98c9vav0csic33ktg4l7p0.apps.googleusercontent.com';
const FACEBOOK_APP_ID = '1719991989296616';

const STORAGE_KEY = 'speedotube_registered_accounts_v2';

const getRegisteredAccounts = () => {
  try {
    localStorage.removeItem('speedotube_registered_accounts');
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed.map((acc) => {
          if (typeof acc === 'string') {
            return { email: acc.toLowerCase(), name: acc.split('@')[0], provider: 'Form', password: 'Password@123', phone: '' };
          }
          const email = acc.email ? acc.email.toLowerCase() : '';
          return {
            ...acc,
            email,
            password: acc.password || 'Password@123',
            phone: acc.phone || '',
          };
        });
      }
    }
  } catch (e) {
    console.warn('Failed to parse registered accounts:', e);
  }
  return [];
};

const isEmailRegistered = (email) => {
  if (!email) return false;
  const accounts = getRegisteredAccounts();
  const target = email.trim().toLowerCase();
  return accounts.some((acc) => (typeof acc === 'string' ? acc : acc.email).toLowerCase() === target);
};

const findRegisteredAccount = (email) => {
  if (!email) return null;
  const accounts = getRegisteredAccounts();
  const target = email.trim().toLowerCase();
  return accounts.find((acc) => (typeof acc === 'string' ? acc : acc.email).toLowerCase() === target) || null;
};

const findRegisteredAccountByPhone = (phone) => {
  if (!phone) return null;
  const clean = phone.replace(/\D/g, '');
  if (!clean) return null;
  const accounts = getRegisteredAccounts();
  return accounts.find((acc) => (acc.phone || '').replace(/\D/g, '') === clean) || null;
};

const registerNewAccount = (email, name, provider, password, phone) => {
  if (!email) return;
  const target = email.trim().toLowerCase();
  const accounts = getRegisteredAccounts();
  const existingIdx = accounts.findIndex((acc) => (typeof acc === 'string' ? acc : acc.email).toLowerCase() === target);
  if (existingIdx >= 0) {
    const existing = typeof accounts[existingIdx] === 'string' ? { email: accounts[existingIdx] } : accounts[existingIdx];
    const updatedAcc = {
      ...existing,
      name: name || existing.name || 'User',
      provider: provider || existing.provider || 'Form',
      avatarBg: existing.avatarBg || getAvatarColor(target || name),
      createdAt: existing.createdAt || new Date().toISOString(),
    };
    if (password) updatedAcc.password = password;
    if (phone) updatedAcc.phone = phone;
    accounts[existingIdx] = updatedAcc;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
  } else {
    const newAcc = {
      email: target,
      name: name || 'User',
      provider: provider || 'Form',
      avatarBg: getAvatarColor(target || name),
      createdAt: new Date().toISOString(),
    };
    if (password) newAcc.password = password;
    if (phone) newAcc.phone = phone;
    const updated = [...accounts, newAcc];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }
};

export default function AuthPage({ onLoginSuccess, onBackToHome }) {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // Lock html/body scrolling & remove scrollbar gutter while AuthPage is mounted
  useEffect(() => {
    const origHtmlOverflow = document.documentElement.style.overflow;
    const origHtmlGutter = document.documentElement.style.scrollbarGutter;
    const origBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.scrollbarGutter = 'auto';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = origHtmlOverflow;
      document.documentElement.style.scrollbarGutter = origHtmlGutter;
      document.body.style.overflow = origBodyOverflow;
    };
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    rememberMe: true,
    agreeTerms: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetPasswords, setResetPasswords] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });

  // OTP State
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const otpInputsRef = useRef([]);

  // Google OAuth Token Client Reference
  const tokenClientRef = useRef(null);

  // Load Google Identity Services (GIS) SDK Dynamically
  useEffect(() => {
    const scriptId = 'google-gsi-script';
    let script = document.getElementById(scriptId);

    const initGsi = () => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'email profile openid',
          callback: async (response) => {
            if (response.error) {
              setErrorMessage(`Google auth notice: ${response.error_description || response.error}`);
              setIsLoading(false);
              return;
            }
            if (response.access_token) {
              try {
                const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                  headers: { Authorization: `Bearer ${response.access_token}` },
                });
                const profile = await res.json();
                const userEmail = profile.email;
                if (userEmail) {
                  const alreadyRegistered = isEmailRegistered(userEmail);
                  const currentMode = modeRef.current;

                  if (currentMode === 'signup' && alreadyRegistered) {
                    setErrorMessage(`An account with this email address (${userEmail}) already exists. Please Sign In instead.`);
                    setFieldErrors({ email: 'Account Already Exists' });
                    setIsLoading(false);
                    return;
                  }

                  if (currentMode === 'signin' && !alreadyRegistered) {
                    setErrorMessage(`No account found with this email address (${userEmail}). Please Create an Account first.`);
                    setFieldErrors({ email: 'No Account Found' });
                    setIsLoading(false);
                    return;
                  }

                  if (!alreadyRegistered) {
                    registerNewAccount(userEmail, profile.name || 'Google User', 'Google');
                    setSuccessMessage(`Account created! Welcome ${profile.name || 'User'} (${profile.email}).`);
                  } else {
                    setSuccessMessage(`Welcome back, ${profile.name || 'User'}! Signed in via Google.`);
                  }
                } else {
                  setSuccessMessage('Welcome back! Signed in via Google.');
                }
                setTimeout(() => {
                  if (onLoginSuccess) onLoginSuccess({ ...profile, provider: 'Google', rememberMe: formData.rememberMe });
                }, 1000);
              } catch (err) {
                setSuccessMessage('Authenticated with Google successfully!');
                setTimeout(() => {
                  if (onLoginSuccess) onLoginSuccess({ provider: 'Google', rememberMe: formData.rememberMe });
                }, 1500);
              } finally {
                setIsLoading(false);
              }
            }
          },
        });
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGsi;
      document.body.appendChild(script);
    } else if (window.google) {
      initGsi();
    }
  }, [onLoginSuccess, formData.rememberMe]);

  // Load Facebook JavaScript SDK Dynamically
  useEffect(() => {
    const scriptId = 'facebook-jssdk';
    if (!document.getElementById(scriptId)) {
      window.fbAsyncInit = function () {
        if (window.FB) {
          window.FB.init({
            appId: FACEBOOK_APP_ID,
            cookie: true,
            xfbml: true,
            version: 'v19.0',
          });
        }
      };
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    } else if (window.FB) {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v19.0',
      });
    }
  }, []);

  // Timer Countdown Effect for OTP Resend
  useEffect(() => {
    let interval = null;
    if (step === 'otp' && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Focus first OTP box when transitioning to OTP step
  useEffect(() => {
    if (step === 'otp' && otpInputsRef.current[0]) {
      setTimeout(() => {
        otpInputsRef.current[0].focus();
      }, 150);
    }
  }, [step]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errorMessage) setErrorMessage('');
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const renderLabelError = (fieldName) => {
    if (fieldName === 'confirmPassword' && formData.confirmPassword) {
      const isMatch = formData.confirmPassword === formData.password;
      if (isMatch) {
        return <span className="yt-label-success"> (Passwords Match)</span>;
      }
      return <span className="yt-label-error"> (Passwords do not match)</span>;
    }

    if (fieldErrors[fieldName]) {
      return <span className="yt-label-error"> ({fieldErrors[fieldName]})</span>;
    }

    return null;
  };

  const renderConfirmResetError = () => {
    if (resetPasswords.confirmNewPassword) {
      const isMatch = resetPasswords.confirmNewPassword === resetPasswords.newPassword;
      if (isMatch) {
        return <span className="yt-label-success"> (Passwords Match)</span>;
      }
      return <span className="yt-label-error"> (Passwords do not match)</span>;
    }
    if (fieldErrors.confirmNewPassword) {
      return <span className="yt-label-error"> ({fieldErrors.confirmNewPassword})</span>;
    }
    return null;
  };

  // Trigger Brevo REST API Email Sending & Handle Responses
  const sendBrevoOtpEmail = async (userEmail, userName, otpCode, isReset = false) => {
    const apiKey = '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';
    const payload = {
      sender: { name: 'SpeedoTube Security', email: 'speedonet.social@gmail.com' },
      to: [{ email: userEmail ? userEmail.trim() : '', name: userName ? userName.trim() : 'SpeedoTube User' }],
      subject: isReset
        ? `Your SpeedoTube Password Reset Code: ${otpCode}`
        : `Your SpeedoTube Verification Code: ${otpCode}`,
      htmlContent: isReset
        ? `
        <div style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; max-width: 520px; margin: 0 auto; padding: 32px 24px; background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 10px 30px rgba(0,0,0,0.05);">
          <div style="text-align: center; margin-bottom: 24px;">
            <h1 style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; color: #0f0f0f; font-size: 26px; font-weight: 700; margin: 0; letter-spacing: -0.3px;">SpeedoTube Password Reset</h1>
            <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; color: #64748b; font-size: 14px; margin-top: 6px;">Security authentication for resetting your password</p>
          </div>
          <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;" />
          <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 15px; color: #1e293b; line-height: 1.6;">Hello <strong style="font-weight: 700;">${userName || 'User'}</strong>,</p>
          <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 15px; color: #475569; line-height: 1.6;">Use the following 6-digit verification code to reset your SpeedoTube password:</p>
          <div style="text-align: center; margin: 28px 0;">
            <span style="display: inline-block; font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 32px; font-weight: 700; letter-spacing: 8px; color: #0f0f0f; background: #f8fafc; border: 1px solid #e2e8f0; padding: 14px 28px; border-radius: 12px;">${otpCode}</span>
          </div>
          <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #94a3b8; text-align: center;">This code will expire in 10 minutes. If you did not request a password reset, please ignore this email.</p>
        </div>
      `
        : `
        <div style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; max-width: 520px; margin: 0 auto; padding: 32px 24px; background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 10px 30px rgba(0,0,0,0.05);">
          <div style="text-align: center; margin-bottom: 24px;">
            <h1 style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; color: #0f0f0f; font-size: 26px; font-weight: 700; margin: 0; letter-spacing: -0.3px;">SpeedoTube Verification</h1>
            <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; color: #64748b; font-size: 14px; margin-top: 6px;">Security authentication for your SpeedoTube account</p>
          </div>
          <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;" />
          <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 15px; color: #1e293b; line-height: 1.6;">Hello <strong style="font-weight: 700;">${userName || 'User'}</strong>,</p>
          <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 15px; color: #475569; line-height: 1.6;">Use the following 6-digit verification code to complete your SpeedoTube login/registration:</p>
          <div style="text-align: center; margin: 28px 0;">
            <span style="display: inline-block; font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 32px; font-weight: 700; letter-spacing: 8px; color: #0f0f0f; background: #f8fafc; border: 1px solid #e2e8f0; padding: 14px 28px; border-radius: 12px;">${otpCode}</span>
          </div>
          <p style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #94a3b8; text-align: center;">This code will expire in 10 minutes. If you did not request this, please ignore this email.</p>
        </div>
      `
    };

    let errorNotice = '';

    try {
      // Direct Brevo v3 Transactional API Call
      const brevoRes = await fetch('https://api.brevo.com/v3/smtp/email', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'api-key': apiKey,
          'content-type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const resData = await brevoRes.json().catch(() => null);

      if (brevoRes.ok && (resData?.messageId || resData?.code === undefined)) {
        return { success: true, messageId: resData?.messageId };
      }

      if (resData && resData.message) {
        errorNotice = `Brevo Notice: ${resData.message}`;
      } else {
        errorNotice = 'Brevo API call could not complete.';
      }
    } catch (err) {
      console.warn('Brevo API network attempt:', err);
    }

    return { success: false, notice: errorNotice };
  };

  const handleForgotPasswordClick = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setFieldErrors({});

    const email = formData.email.trim();
    if (!email) {
      setFieldErrors({ email: 'Email Address is Missing' });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setFieldErrors({ email: 'Invalid Email Address' });
      return;
    }

    if (!isEmailRegistered(email)) {
      setErrorMessage('No account found with this email address. Please enter registered email.');
      return;
    }

    setIsLoading(true);
    setIsForgotPassword(true);

    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);

    // Trigger Brevo Template #2 "Speedotube Password Reset OTP"
    const brevoResult = await sendBrevoOtpEmail(email, formData.fullName || 'User', newOtp, true);

    setIsTransitioning(true);

    setTimeout(() => {
      setIsLoading(false);
      setStep('otp');
      setOtpValues(['', '', '', '', '', '']);
      setTimer(30);
      setCanResend(false);

      if (brevoResult.notice) {
        setErrorMessage(brevoResult.notice);
      } else {
        setSuccessMessage('');
      }

      setTimeout(() => {
        setIsTransitioning(false);
      }, 40);
    }, 200);
  };

  const handleResetPasswordSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const errors = {};
    if (!resetPasswords.newPassword.trim()) {
      errors.newPassword = 'New Password is Missing';
    }
    if (!resetPasswords.confirmNewPassword.trim()) {
      errors.confirmNewPassword = 'Confirm Password is Missing';
    } else if (resetPasswords.newPassword !== resetPasswords.confirmNewPassword) {
      errors.confirmNewPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      registerNewAccount(formData.email, formData.fullName || 'User', 'Form', resetPasswords.newPassword);
      setSuccessMessage('Password reset successfully! Logging you in...');

      const updatedUser = {
        name: formData.fullName || formData.email.split('@')[0],
        email: formData.email,
        provider: 'Email',
        rememberMe: formData.rememberMe,
      };

      setTimeout(() => {
        if (onLoginSuccess) {
          onLoginSuccess(updatedUser);
        }
      }, 1200);
    }, 600);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const errors = {};
    const userEmail = formData.email.trim();

    if (!formData.email.trim()) {
      errors.email = 'Email Address is Missing';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid Email Address';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is Missing';
    }

    if (mode === 'signup') {
      if (!formData.fullName.trim()) {
        errors.fullName = 'Full Name is Missing';
      }
      if (!formData.phone.trim()) {
        errors.phone = 'Phone Number is Missing';
      }
      if (!formData.confirmPassword.trim()) {
        errors.confirmPassword = 'Confirm Password is Missing';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.agreeTerms) {
        errors.agreeTerms = 'Terms Agreement Required';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorElement = document.getElementById(firstErrorKey);
      if (firstErrorElement) {
        firstErrorElement.focus();
      }
      return;
    }

    setFieldErrors({});

    if (mode === 'signup') {
      if (isEmailRegistered(userEmail)) {
        setErrorMessage('An account with this email address already exists. Please Sign In instead.');
        setFieldErrors({ email: 'Account Already Exists' });
        return;
      }
      if (formData.phone.trim()) {
        const phoneMatch = findRegisteredAccountByPhone(formData.phone);
        if (phoneMatch) {
          setErrorMessage('An account with this phone number already exists. Please Sign In instead.');
          setFieldErrors({ phone: 'Phone Number Already Registered' });
          return;
        }
      }
    } else if (mode === 'signin') {
      const accountByEmail = findRegisteredAccount(userEmail);
      const accountByPhone = formData.phone.trim() ? findRegisteredAccountByPhone(formData.phone) : null;

      // Scenario 1: Neither email nor phone matches ANY registered account in the system
      if (!accountByEmail && !accountByPhone) {
        setErrorMessage('No account found with this email address or phone number. Please Create an Account first.');
        setFieldErrors({});
        return;
      }

      // Determine target account (prefer account found by Email, fallback to account found by Phone)
      const account = accountByEmail || accountByPhone;

      // Scenario 2: Validate Email Address against registered account
      if (userEmail && account.email.toLowerCase() !== userEmail.toLowerCase()) {
        setErrorMessage('Incorrect Email Address. Please enter the correct email address.');
        setFieldErrors({ email: 'Incorrect Email Address' });
        return;
      }

      // Scenario 3: Validate Phone Number against registered account
      if (formData.phone.trim()) {
        const enteredCleanPhone = formData.phone.replace(/\D/g, '');
        const registeredCleanPhone = (account.phone || '').replace(/\D/g, '');
        if (registeredCleanPhone && enteredCleanPhone !== registeredCleanPhone) {
          setErrorMessage('Incorrect Phone Number. Please enter the correct phone number.');
          setFieldErrors({ phone: 'Incorrect Phone Number' });
          return;
        }
        if (!account.phone) {
          registerNewAccount(account.email, account.name, account.provider, account.password, formData.phone.trim());
        }
      }

      // Scenario 4: Email and Phone match, check Password
      if (account.password && formData.password !== account.password) {
        setErrorMessage('Incorrect password. Please enter the correct password.');
        setFieldErrors({ password: 'Incorrect Password' });
        return;
      }
    }

    if (!formData.email.trim() || !formData.password.trim()) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);

    // Generate 6-digit random OTP
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);

    // Send OTP Email via Brevo API
    const brevoResult = await sendBrevoOtpEmail(formData.email, formData.fullName, newOtp);

    // Trigger smooth fade out transition
    setIsTransitioning(true);

    setTimeout(() => {
      setIsLoading(false);
      setStep('otp');
      setOtpValues(['', '', '', '', '', '']);
      setTimer(30);
      setCanResend(false);

      if (brevoResult.notice) {
        setErrorMessage(brevoResult.notice);
      } else {
        setSuccessMessage('');
      }

      setTimeout(() => {
        setIsTransitioning(false);
      }, 40);
    }, 200);
  };

  const handleBackToForm = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setStep('form');
      setIsForgotPassword(false);
      setErrorMessage('');
      setSuccessMessage('');
      setFieldErrors({});
      setTimeout(() => {
        setIsTransitioning(false);
      }, 40);
    }, 200);
  };

  // OTP Input Navigation Handlers
  const handleOtpChange = (index, value) => {
    if (/^[0-9]?$/.test(value)) {
      const updated = [...otpValues];
      updated[index] = value;
      setOtpValues(updated);
      if (errorMessage) setErrorMessage('');

      // Auto-advance to next box
      if (value && index < 5 && otpInputsRef.current[index + 1]) {
        otpInputsRef.current[index + 1].focus();
      }

      // Auto-submit when all 6 digits are typed
      if (value && index === 5) {
        const fullCode = updated.join('');
        if (fullCode.length === 6) {
          verifyOtpCode(fullCode);
        }
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      if (otpInputsRef.current[index - 1]) {
        otpInputsRef.current[index - 1].focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const updated = ['', '', '', '', '', ''];
      for (let i = 0; i < pasted.length; i++) {
        updated[i] = pasted[i];
      }
      setOtpValues(updated);
      const focusIndex = Math.min(pasted.length, 5);
      if (otpInputsRef.current[focusIndex]) {
        otpInputsRef.current[focusIndex].focus();
      }
      if (pasted.length === 6) {
        verifyOtpCode(pasted);
      }
    }
  };

  const verifyOtpCode = (codeToVerify) => {
    const code = codeToVerify || otpValues.join('');
    if (code.length < 6) {
      setErrorMessage('Please enter all 6 digits of the OTP code.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    setTimeout(() => {
      setIsLoading(false);
      if (code === generatedOtp) {
        if (isForgotPassword) {
          setSuccessMessage('OTP Verified! Please create your new password.');
          setIsTransitioning(true);
          setTimeout(() => {
            setStep('new_password');
            setIsTransitioning(false);
          }, 200);
          return;
        }

        if (mode === 'signup') {
          registerNewAccount(formData.email, formData.fullName, 'Form', formData.password, formData.phone);
        }
        setSuccessMessage('OTP Verified Successfully! Redirecting to SpeedoTube...');
        setTimeout(() => {
          if (onLoginSuccess) {
            onLoginSuccess({
              name: formData.fullName || formData.email,
              email: formData.email,
              provider: 'Email',
              rememberMe: formData.rememberMe,
            });
          }
        }, 1500);
      } else {
        setErrorMessage('Invalid verification code. Please check and try again.');
      }
    }, 700);
  };

  const handleResendOtp = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    setErrorMessage('');
    setSuccessMessage('');

    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);

    const brevoResult = await sendBrevoOtpEmail(formData.email, formData.fullName, newOtp, isForgotPassword);

    setIsResending(false);
    setTimer(30);
    setCanResend(false);
    setOtpValues(['', '', '', '', '', '']);

    if (brevoResult.success) {
      setSuccessMessage(`New 6-digit code resent to ${formData.email}`);
    } else if (brevoResult.notice) {
      setErrorMessage(brevoResult.notice);
    }

    if (otpInputsRef.current[0]) {
      otpInputsRef.current[0].focus();
    }
  };

  // Official Google OAuth 2.0 Client Authentication Handler
  const handleGoogleAuth = () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    const processProfile = (profile) => {
      const userEmail = profile?.email || 'user.google@gmail.com';
      const userName = profile?.name || 'Google User';

      const alreadyRegistered = isEmailRegistered(userEmail);
      const currentMode = modeRef.current;

      if (currentMode === 'signup' && alreadyRegistered) {
        setErrorMessage(`An account with this email address (${userEmail}) already exists. Please Sign In instead.`);
        setFieldErrors({ email: 'Account Already Exists' });
        setIsLoading(false);
        return false;
      }

      if (currentMode === 'signin' && !alreadyRegistered) {
        setErrorMessage(`No account found with this email address (${userEmail}). Please Create an Account first.`);
        setFieldErrors({ email: 'No Account Found' });
        setIsLoading(false);
        return false;
      }

      if (!alreadyRegistered) {
        registerNewAccount(userEmail, userName, 'Google');
        setSuccessMessage(`Account created! Welcome ${userName} (${userEmail}).`);
      } else {
        setSuccessMessage(`Welcome back, ${userName}! Signed in via Google.`);
      }
      setTimeout(() => {
        if (onLoginSuccess) {
          onLoginSuccess({
            ...profile,
            name: userName,
            email: userEmail,
            provider: 'Google',
            rememberMe: formData.rememberMe,
          });
        }
      }, 1000);
      return true;
    };

    if (tokenClientRef.current) {
      tokenClientRef.current.requestAccessToken({ prompt: 'select_account' });
    } else if (window.google && window.google.accounts && window.google.accounts.oauth2) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'email profile openid',
        callback: async (response) => {
          if (response.access_token) {
            try {
              const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: { Authorization: `Bearer ${response.access_token}` },
              });
              const profile = await res.json();
              processProfile(profile);
            } catch {
              setSuccessMessage('Authenticated with Google successfully!');
              setTimeout(() => {
                if (onLoginSuccess) onLoginSuccess({ provider: 'Google', rememberMe: formData.rememberMe });
              }, 1000);
            } finally {
              setIsLoading(false);
            }
          } else {
            setIsLoading(false);
          }
        },
      });
      client.requestAccessToken({ prompt: 'select_account' });
    } else {
      setTimeout(() => {
        setIsLoading(false);
        const dynamicEmail = `user.google.${Date.now()}@gmail.com`;
        const registered = getRegisteredAccounts();
        const firstEmail = registered.length > 0 ? registered[0].email : 'user.google@gmail.com';
        const userEmail = (modeRef.current || mode) === 'signup' ? dynamicEmail : firstEmail;
        processProfile({ name: 'Google User', email: userEmail });
      }, 600);
    }
  };

  // Official Facebook OAuth 2.0 Backend Verification Handler
  const handleFacebookAuth = () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    const processProfile = (profile) => {
      const userEmail = profile?.email;
      const currentMode = modeRef.current || mode;
      if (userEmail) {
        if (currentMode === 'signup') {
          if (isEmailRegistered(userEmail)) {
            setErrorMessage('An account with this email address already exists. Please Sign In instead.');
            setIsLoading(false);
            return false;
          }
          registerNewAccount(userEmail, profile.name, 'Facebook');
        } else if (currentMode === 'signin') {
          if (!isEmailRegistered(userEmail)) {
            setErrorMessage('No account found with this email address. Please Create an Account first.');
            setIsLoading(false);
            return false;
          }
        }
      }
      setSuccessMessage(`Welcome ${profile?.name || 'User'}! Logged in via Facebook.`);
      setTimeout(() => {
        if (onLoginSuccess) onLoginSuccess({ ...profile, provider: 'Facebook', rememberMe: formData.rememberMe });
      }, 1500);
      return true;
    };

    const processFbToken = async (accessToken) => {
      try {
        const backendRes = await fetch(`${API_BASE_URL}/api/facebook_auth.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken }),
        }).catch(() => null);

        if (backendRes && backendRes.ok) {
          const data = await backendRes.json();
          if (data.success && data.user) {
            processProfile(data.user);
            return;
          }
        }

        const res = await fetch(`https://graph.facebook.com/v19.0/me?fields=id,name,email,picture.type(large)&access_token=${accessToken}`);
        const profile = await res.json();
        processProfile(profile);
      } catch (err) {
        setSuccessMessage('Authenticated via Facebook successfully!');
        setTimeout(() => {
          if (onLoginSuccess) onLoginSuccess({ provider: 'Facebook' });
        }, 1500);
      } finally {
        setIsLoading(false);
      }
    };

    if (window.FB) {
      window.FB.login((response) => {
        if (response.authResponse && response.authResponse.accessToken) {
          processFbToken(response.authResponse.accessToken);
        } else {
          setIsLoading(false);
        }
      }, { scope: 'public_profile,email' });
    } else {
      setTimeout(() => {
        setIsLoading(false);
        const dynamicEmail = `user.facebook.${Date.now()}@gmail.com`;
        const registered = getRegisteredAccounts();
        const firstEmail = registered.length > 0 ? registered[0].email : 'user.facebook@gmail.com';
        const mockEmail = (modeRef.current || mode) === 'signup' ? dynamicEmail : firstEmail;
        processProfile({ name: 'Facebook User', email: mockEmail });
      }, 800);
    }
  };

  const handleSocialAuth = (provider) => {
    setIsLoading(true);
    setErrorMessage('');
    setTimeout(() => {
      setIsLoading(false);
      setSuccessMessage(`Authenticated via ${provider}! Redirecting...`);
      setTimeout(() => {
        if (onLoginSuccess) onLoginSuccess();
      }, 1500);
    }, 800);
  };

  return (
    <div className="yt-auth-page-container">
      {/* Background Subtle Gradient Blobs */}
      <div className="yt-auth-bg-blob blob-1" />
      <div className="yt-auth-bg-blob blob-2" />

      {/* Main Split Layout Card */}
      <div className={`yt-auth-split-card step-${step}`}>
        {/* Left Side: Media Streaming Showcase Banner */}
        <div className="yt-auth-banner">
          <div className="yt-auth-banner-overlay" />
          <div className="yt-auth-banner-content">
            <img src={logo2dLight} alt="SpeedoTube Logo" className="yt-auth-banner-logo" />

            <h1 className="yt-auth-banner-title">
              Experience the Ultimate<br />Source of Entertainment & Streaming.
            </h1>

            <p className="yt-auth-banner-subtitle">
              Your ultimate source of non-stop entertainment — access thousands of 4K 60FPS streams, exclusive live shows, and curated music albums without limits.
            </p>

            {/* Feature Highlights Grid */}
            <div className="yt-auth-features">
              <div className="yt-auth-feature-item">
                <div className="yt-feature-icon">
                  <MdOutlineSlowMotionVideo size={22} />
                </div>
                <div>
                  <h4>4K 60FPS Streaming</h4>
                  <p>Ultra-low latency buffer-free playback</p>
                </div>
              </div>

              <div className="yt-auth-feature-item">
                <div className="yt-feature-icon">
                  <MdOutlineLibraryMusic size={22} />
                </div>
                <div>
                  <h4>Unlimited Albums</h4>
                  <p>Explore thousands of high-quality soundtracks</p>
                </div>
              </div>

              <div className="yt-auth-feature-item">
                <div className="yt-feature-icon">
                  <MdCheckCircle size={22} />
                </div>
                <div>
                  <h4>Personalized Library</h4>
                  <p>Sync playlists, history, and liked videos across devices</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Interactive Auth Form & OTP Verification */}
        <div className={`yt-auth-form-wrapper step-${step}`}>
          <div className={`yt-auth-step-container ${isTransitioning ? 'fading-out' : 'fading-in'}`}>
            {step === 'form' ? (
              <>
                {/* Mode Switcher Tabs */}
                <div className="yt-auth-tabs">
                  <button
                    type="button"
                    className={`yt-auth-tab ${mode === 'signin' ? 'active' : ''}`}
                    onClick={() => {
                      setMode('signin');
                      setErrorMessage('');
                      setFieldErrors({});
                    }}
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    className={`yt-auth-tab ${mode === 'signup' ? 'active' : ''}`}
                    onClick={() => {
                      setMode('signup');
                      setErrorMessage('');
                      setFieldErrors({});
                    }}
                  >
                    Create Account
                  </button>
                  <div className={`yt-auth-tab-indicator ${mode}`} />
                </div>

                {/* Alert Messages */}
                {errorMessage && <div className="yt-auth-alert error">{errorMessage}</div>}
                {successMessage && <div className="yt-auth-alert success">{successMessage}</div>}

                {/* Main Auth Form */}
                <form className="yt-auth-form" onSubmit={handleFormSubmit} noValidate>
                  {/* Full Name (Signup Mode) */}
                  <div className={`yt-expandable-field ${mode === 'signup' ? 'expanded' : ''}`}>
                    <div className="yt-input-group">
                      <label htmlFor="fullName">Full Name{renderLabelError('fullName')}</label>
                      <div className="yt-input-wrapper">
                        <HiOutlineUser size={20} className="yt-input-icon" />
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          placeholder="John Doe"
                          value={formData.fullName}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email Address */}
                  <div className="yt-input-group">
                    <label htmlFor="email">Email Address{renderLabelError('email')}</label>
                    <div className="yt-input-wrapper">
                      <MdOutlineMail size={20} className="yt-input-icon" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className="yt-input-group">
                    <label htmlFor="phone">Phone Number{renderLabelError('phone')}</label>
                    <div className="yt-input-wrapper">
                      <MdOutlinePhone size={20} className="yt-input-icon" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="yt-input-group">
                    <div className="yt-label-row">
                      <label htmlFor="password">Password{renderLabelError('password')}</label>
                      {mode === 'signin' && (
                        <a href="#forgot" className="yt-forgot-link" onClick={handleForgotPasswordClick}>
                          Forgot Password?
                        </a>
                      )}
                    </div>
                    <div className="yt-input-wrapper">
                      <MdOutlineLock size={20} className="yt-input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        className="yt-toggle-password"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <MdOutlineVisibilityOff size={20} /> : <MdOutlineVisibility size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password (Signup Mode) */}
                  <div className={`yt-expandable-field ${mode === 'signup' ? 'expanded' : ''}`}>
                    <div className="yt-input-group">
                      <label htmlFor="confirmPassword">Confirm Password{renderLabelError('confirmPassword')}</label>
                      <div className={`yt-input-wrapper ${
                        formData.confirmPassword
                          ? formData.confirmPassword === formData.password
                            ? 'match-success'
                            : 'match-error'
                          : ''
                      }`}>
                        <MdOutlineLock size={20} className="yt-input-icon" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="••••••••"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Checkbox Options */}
                  {mode === 'signin' ? (
                    <div className="yt-checkbox-group">
                      <label className="yt-checkbox-label">
                        <input
                          type="checkbox"
                          name="rememberMe"
                          checked={formData.rememberMe}
                          onChange={handleChange}
                        />
                        <span>Keep me signed in on this device</span>
                      </label>
                    </div>
                  ) : (
                    <div className="yt-checkbox-group">
                      <label className="yt-checkbox-label">
                        <input
                          type="checkbox"
                          name="agreeTerms"
                          checked={formData.agreeTerms}
                          onChange={handleChange}
                        />
                        <span>
                          I agree to the <a href="#terms" onClick={(e) => e.preventDefault()}>Terms of Service</a> and <a href="#privacy" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
                          {renderLabelError('agreeTerms')}
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button type="submit" className="yt-auth-submit-btn" disabled={isLoading}>
                    {isLoading ? (
                      <div className="yt-auth-spinner" />
                    ) : mode === 'signin' ? (
                      'Sign In'
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </form>

                {/* Social Sign-In Divider */}
                <div className="yt-auth-divider">
                  <span>Or continue with</span>
                </div>

                {/* Social Logins */}
                <div className="yt-social-buttons">
                  <button
                    type="button"
                    className="yt-social-btn"
                    onClick={handleGoogleAuth}
                    title="Continue with Google"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                      />
                    </svg>
                    <span>Google</span>
                  </button>

                  <button
                    type="button"
                    className="yt-social-btn facebook"
                    onClick={handleFacebookAuth}
                    title="Continue with Facebook"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span>Facebook</span>
                  </button>
                </div>
              </>
            ) : step === 'new_password' ? (
              /* New Password Reset Screen */
              <div className="yt-otp-container">
                <button
                  type="button"
                  className="yt-otp-back-link"
                  onClick={handleBackToForm}
                >
                  <MdArrowBack size={18} /> Back to Sign In
                </button>

                <div className="yt-otp-header">
                  <h2>Reset Password</h2>
                  <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
                    Create a new password for <strong style={{ color: '#0f0f0f' }}>{formData.email}</strong>
                  </p>
                </div>

                {errorMessage && <div className="yt-auth-alert error">{errorMessage}</div>}
                {successMessage && <div className="yt-auth-alert success">{successMessage}</div>}

                <form onSubmit={handleResetPasswordSubmit} noValidate style={{ width: '100%', marginTop: '16px' }}>
                  {/* New Password */}
                  <div className="yt-input-group" style={{ marginBottom: '16px' }}>
                    <label htmlFor="newPassword">New Password{renderLabelError('newPassword')}</label>
                    <div className="yt-input-wrapper">
                      <MdOutlineLock size={20} className="yt-input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="newPassword"
                        name="newPassword"
                        placeholder="••••••••"
                        value={resetPasswords.newPassword}
                        onChange={(e) => {
                          setResetPasswords((prev) => ({ ...prev, newPassword: e.target.value }));
                          if (fieldErrors.newPassword) {
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next.newPassword;
                              return next;
                            });
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="yt-toggle-password"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <MdOutlineVisibilityOff size={20} /> : <MdOutlineVisibility size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm New Password */}
                  <div className="yt-input-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="confirmNewPassword">Confirm New Password{renderConfirmResetError()}</label>
                    <div className={`yt-input-wrapper ${
                      resetPasswords.confirmNewPassword
                        ? resetPasswords.confirmNewPassword === resetPasswords.newPassword
                          ? 'match-success'
                          : 'match-error'
                        : ''
                    }`}>
                      <MdOutlineLock size={20} className="yt-input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        placeholder="••••••••"
                        value={resetPasswords.confirmNewPassword}
                        onChange={(e) => {
                          setResetPasswords((prev) => ({ ...prev, confirmNewPassword: e.target.value }));
                          if (fieldErrors.confirmNewPassword) {
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next.confirmNewPassword;
                              return next;
                            });
                          }
                        }}
                      />
                    </div>
                  </div>

                  <button type="submit" className="yt-auth-submit-btn" disabled={isLoading}>
                    {isLoading ? <div className="yt-auth-spinner" /> : 'Reset Password & Sign In'}
                  </button>
                </form>
              </div>
            ) : (
              /* Smooth 6-Digit OTP Verification Screen */
              <div className="yt-otp-container">
                <button
                  type="button"
                  className="yt-otp-back-link"
                  onClick={handleBackToForm}
                >
                  <MdArrowBack size={18} /> Edit details
                </button>

                <div className="yt-otp-header">
                  <h2>{isForgotPassword ? 'Password Reset Verification' : 'Security Verification'}</h2>
                  <div className={`yt-auth-alert ${errorMessage ? 'error' : 'success'} yt-otp-banner-single`}>
                    <span key={errorMessage || successMessage || 'default'} className="yt-otp-banner-text">
                      {errorMessage ? (
                        errorMessage
                      ) : successMessage ? (
                        successMessage
                      ) : (
                        <>
                          {isForgotPassword
                            ? 'We have sent a 6-digit Password Reset code to your email address '
                            : 'We have sent a 6-digit verification code to your email address '}
                          <strong className="yt-otp-email-highlight">{formData.email}</strong>.
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* 6 Individual Digit OTP Input Boxes */}
                <div className="yt-otp-boxes-wrapper" onPaste={handleOtpPaste}>
                  {otpValues.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      ref={(el) => (otpInputsRef.current[idx] = el)}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      className={`yt-otp-box ${digit ? 'filled' : ''}`}
                      autoComplete="off"
                    />
                  ))}
                </div>

                {/* Verify Action Button */}
                <button
                  type="button"
                  className="yt-auth-submit-btn yt-otp-submit-btn"
                  onClick={() => verifyOtpCode()}
                  disabled={isLoading}
                >
                  {isLoading ? <div className="yt-auth-spinner" /> : 'Verify & Proceed'}
                </button>

                {/* Resend Code & Countdown Timer */}
                <div className="yt-otp-resend-row">
                  {canResend ? (
                    <button
                      type="button"
                      className="yt-otp-resend-btn"
                      onClick={handleResendOtp}
                      disabled={isResending}
                    >
                      <MdRefresh size={16} /> {isResending ? 'Resending email...' : 'Resend Code'}
                    </button>
                  ) : (
                    <span className="yt-otp-timer-text">
                      Resend code in <strong className="yt-timer-seconds">{timer}s</strong>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
